import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
} from 'react-native';
import { useAppDispatch } from '../store/hooks';
import { login, enterGuestMode, saveUserSession, loadUserSession } from '../store/authSlice';
import AuthService from '../services/auth';
import CellphoneLoginScreen from './CellphoneLoginScreen';
import EmailLoginScreen from './EmailLoginScreen';
import NicknameLoginScreen from './NicknameLoginScreen';

const LoginScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const [googleLoading, setGoogleLoading] = React.useState(false);
  const [appleLoading] = React.useState(false);
  const [showCellphoneLogin, setShowCellphoneLogin] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [showNicknameLogin, setShowNicknameLogin] = useState(false);

  useEffect(() => {
    // 先尝试从本地存储恢复登录状态
    const restoreSession = async () => {
      try {
        await dispatch(loadUserSession());
      } catch (error) {
        console.error('Failed to restore session:', error);
      }
    };

    restoreSession();

    // 监听认证状态变化
    const { data: { subscription } } = AuthService.onAuthStateChange((user) => {
      if (user) {
        console.log('Auth state changed - user logged in:', user);
        dispatch(login(user));
      } else {
        console.log('Auth state changed - user logged out');
        // 可以在这里处理登出逻辑
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [dispatch]);

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      console.log('=== Starting Google Sign In with Supabase ===');

      const user = await AuthService.signInWithGoogle();
      console.log('=== Supabase login success ===', user);

      dispatch(saveUserSession(user));
      Alert.alert('成功', '登录成功！');
    } catch (error: any) {
      console.error('=== Google login error ===', error);
      Alert.alert('错误', `Google登录失败：${error.message || '未知错误'}`);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    try {
      // TODO: 实现Apple登录
      Alert.alert('提示', 'Apple登录功能开发中');
    } catch (error) {
      console.error('Apple login error:', error);
      Alert.alert('错误', 'Apple登录失败，请重试');
    }
  };

  if (showNicknameLogin) {
    return (
      <NicknameLoginScreen onBack={() => setShowNicknameLogin(false)} />
    );
  }

  if (showCellphoneLogin) {
    return (
      <CellphoneLoginScreen onBack={() => setShowCellphoneLogin(false)} />
    );
  }

  if (showEmailLogin) {
    return (
      <EmailLoginScreen onBack={() => setShowEmailLogin(false)} />
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <Text style={styles.title}>一季聊烂熟</Text>
        <Text style={styles.subtitle}>ChatFluent</Text>
        <Text style={styles.description}>简单注册，立即开始学习</Text>

        <TouchableOpacity
          style={[styles.button, styles.nicknameButton]}
          onPress={() => setShowNicknameLogin(true)}
        >
          <Text style={styles.buttonText}>🎮 开始学习</Text>
          <Text style={styles.buttonSubtext}>昵称 + 密码即可</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.alternativeButton}
          onPress={() => setShowEmailLogin(true)}
        >
          <Text style={styles.alternativeButtonText}>使用其他方式登录</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f0f0f0',
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  title: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 20,
  },
  subtitle: {
    fontSize: 20,
    color: '#4285F4',
    textAlign: 'center',
    fontWeight: '600',
    marginBottom: 10,
  },
  description: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 40,
  },
  button: {
    width: '100%',
    paddingVertical: 15,
    borderRadius: 25,
    alignItems: 'center',
    marginBottom: 15,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
  },
  nicknameButton: {
    backgroundColor: '#4285F4',
    paddingVertical: 18,
    marginBottom: 30,
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  buttonSubtext: {
    color: '#fff',
    fontSize: 12,
    opacity: 0.8,
    marginTop: 2,
  },
  alternativeButton: {
    marginTop: 10,
    padding: 15,
    alignItems: 'center',
  },
  alternativeButtonText: {
    color: '#666',
    fontSize: 14,
    textDecorationLine: 'underline',
  },
});

export default LoginScreen;
